import { useMemo, useState } from "react";
import { ChevronDown, Check, Lock } from "lucide-react";
import { PERM_ACTIONS, buildPermGroups, moduleLabel } from "../../lib/permGroups";

const Cell = ({ on, disabled, onClick, testid }) => (
  <button type="button" data-testid={testid} disabled={disabled} onClick={onClick}
    className={`mx-auto grid h-6 w-6 place-items-center rounded-md border transition-[background-color,border-color,transform] duration-150 active:scale-90 ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-transparent hover:border-blue-400"} ${disabled ? "cursor-default opacity-70" : ""}`}>
    <Check className="h-3.5 w-3.5" strokeWidth={3} />
  </button>
);

const setCell = (matrix, m, a, on) => {
  const row = { ...(matrix[m] || {}) };
  row[a] = on;
  if (a !== "view" && on) row.view = true;
  if (a === "view" && !on) PERM_ACTIONS.forEach((x) => { row[x.id] = false; });
  const next = { ...matrix };
  if (PERM_ACTIONS.some((x) => row[x.id])) next[m] = row; else delete next[m];
  return next;
};

export const PermMatrix = ({ value, onChange, modules, t, testid = "perm-matrix", lockedMsg = "دور النظام للعرض فقط — انسخه كدور مخصص لتعديله" }) => {
  const groups = useMemo(() => buildPermGroups(modules, t), [modules, t]);
  const [closed, setClosed] = useState({});
  const ro = !onChange;
  const m = value || {};
  const all = (keys, a) => keys.every((k) => m[k]?.[a]);
  const toggleMany = (keys, a) => {
    const on = !all(keys, a);
    onChange(keys.reduce((acc, k) => setCell(acc, k, a, on), m));
  };
  const allKeys = groups.flatMap((g) => g.keys);
  return (
    <div data-testid={testid} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {ro && <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> {lockedMsg}</div>}
      <div className="max-h-[52vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur">
            <tr className="border-b border-slate-200 text-xs text-slate-500">
              <th className="px-4 py-2.5 text-start font-semibold">القسم</th>
              {PERM_ACTIONS.map((a) => (
                <th key={a.id} className="w-16 px-1 py-2.5 font-semibold">
                  <button type="button" disabled={ro} data-testid={`${testid}-col-${a.id}`} onClick={() => toggleMany(allKeys, a.id)}
                    className={`rounded-md px-1.5 py-0.5 ${ro ? "" : "hover:bg-blue-50 hover:text-blue-700"}`}>{a.label}</button>
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.id}>
              <tr className="bg-slate-50/80">
                <td className="px-4 py-2">
                  <button type="button" onClick={() => setClosed((c) => ({ ...c, [g.id]: !c[g.id] }))} data-testid={`${testid}-group-${g.id}`}
                    className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${closed[g.id] ? "-rotate-90 rtl:rotate-90" : ""}`} /> {g.label}
                    <span className="font-normal text-slate-400">({g.keys.length})</span>
                  </button>
                </td>
                {PERM_ACTIONS.map((a) => (
                  <td key={a.id} className="py-1.5"><Cell on={all(g.keys, a.id)} disabled={ro} onClick={() => toggleMany(g.keys, a.id)} testid={`${testid}-g-${g.id}-${a.id}`} /></td>
                ))}
              </tr>
              {!closed[g.id] && g.keys.map((k) => (
                <tr key={k} className="border-t border-slate-100 hover:bg-blue-50/30">
                  <td className="px-4 py-1.5 ps-9 text-slate-700">{moduleLabel(k, t)}</td>
                  {PERM_ACTIONS.map((a) => (
                    <td key={a.id} className="py-1.5"><Cell on={Boolean(m[k]?.[a.id])} disabled={ro} onClick={() => onChange(setCell(m, k, a.id, !m[k]?.[a.id]))} testid={`${testid}-${k}-${a.id}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
};
